import { AppNS } from 'app';
import { apiAddTicket } from 'app/api_v2/tickets';
import { can } from 'app/helpers/can';
import ChipList, { ChipsListItem } from 'app/ui_v2/components/chip-list/ChipList';
import { ConfirmationDialog } from 'app/ui_v2/components/confirmation-dialog/ConfirmationDialog';
import ErrorsList from 'app/ui_v2/components/errors-list/ErrorsList';
import MapsLink from 'app/ui_v2/components/maps-link/MapsLink';
import { getUserRoleInfo } from 'app/ui_v2/helpers/users';
import AlertTriangle from 'app/ui_v2/icons/AlertTriangle';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';

const checkIsGeolocationFailed = (item: AppNS.JobEventReport): boolean => {
  const responses = item.protocoleResponses?.filter(
    (response) => response.type === 'check_in' || response.type === 'check_out'
  );
  return !responses?.some((response) => response.lat && response.lng);
};

const getFirstFailedResponse = (item: AppNS.JobEventReport): AppNS.ProtocolResponse | undefined => {
  return item.protocoleResponses?.find(
    (response) => (response.type === 'check_in' || response.type === 'check_out') && (!response.lat || !response.lng)
  );
};

export interface GeolocationProps {
  item: AppNS.JobEventReport;
}

const MISSING_ITEM: ChipsListItem[] = [
  {
    id: '1',
    name: (
      <Box display="flex" alignItems="center" gap={1}>
        <Box display="flex" alignItems="center">
          <AlertTriangle sx={{ fontSize: '2rem' }} />
        </Box>
        Missing
      </Box>
    ),
    tooltip: 'Missing Geolocation Data',
  },
];

export default function Geolocation(props: GeolocationProps) {
  const { item } = props;

  const mutation = useMutation({
    mutationFn: (data: Partial<AppNS.Ticket>) => apiAddTicket(data),
    onSuccess: (data: AppNS.Ticket) => {
      // no need to support Backoffice View because this will be created by Agency Users on Agency Inspections listing view only
      navigate(`/agency/tickets/${data.id}`);
    },
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const navigate = useNavigate();

  const { currentUser } = getUserRoleInfo();

  if (!currentUser) {
    return null;
  }

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (id: string) => {
    if (id && typeof id === 'string') {
      setDialogOpen(true);
    }
    setAnchorEl(null);
  };

  if (!item.protocoleResponses || item.protocoleResponses.length === 0) {
    return <>N/A</>;
  }

  const isFailed = checkIsGeolocationFailed(item);

  const handleDataConfirm = () => {
    const failedResponse = getFirstFailedResponse(item);
    if (!mutation.isLoading && failedResponse) {
      mutation.mutate({
        protocoleResponseId: failedResponse.id,
        ticketType: 'dispute_missing_geolocation',
        state: 'subject_action_required',
      });
    }
  };

  const canUserEdit = can(currentUser, 'manage_company_profile', item.agencyCompany?.id);

  return (
    <>
      {isFailed && !canUserEdit && (
        <>
          <Box my={-2}>
            <ChipList list={MISSING_ITEM} isClickable={true} classNames="error-outlined" />
          </Box>
        </>
      )}
      {isFailed && canUserEdit && (
        <>
          <Button
            variant="outlined"
            className="error"
            color="primary"
            disabled={false}
            aria-label="Create Ticket"
            onClick={handleClick}
            endIcon={
              <>
                {!open && <KeyboardArrowDownIcon />} {open && <KeyboardArrowUpIcon />}
              </>
            }
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Box display="flex" alignItems="center">
                <AlertTriangle sx={{ fontSize: '2rem' }} />
              </Box>
              Missing
            </Box>
          </Button>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
          >
            <MenuItem onClick={() => handleClose('create-ticket')}>Create Ticket</MenuItem>
          </Menu>
          <ConfirmationDialog
            title=""
            description={
              <Box mb={2}>
                <Box mt={2} textAlign="center">
                  <Typography component="h2" variant="h2">
                    Ticket Creation Confirmation
                  </Typography>
                  <Typography component="h3" variant="h4">
                    Are you sure you want to create a ticket for missing geolocation data?
                  </Typography>
                  {mutation.isError && mutation.error && (
                    <Box mt={2}>
                      <ErrorsList title="Create ticket error" errors={mutation.error as string[]} />
                    </Box>
                  )}
                </Box>
              </Box>
            }
            opened={dialogOpen}
            confirmButtonVariant="contained"
            confirmButtonClass="secondary"
            onConfirm={() => {
              handleDataConfirm();
            }}
            onCancel={() => {
              mutation.reset();
              setDialogOpen(false);
            }}
            confirmButtonText="Create Ticket"
            isLoading={mutation.isLoading}
          />
        </>
      )}
      {!isFailed &&
        item.protocoleResponses
          ?.filter((response) => response.lat && (response.type === 'check_in' || response.type === 'check_out'))
          .map((response) => (
            <Box sx={{ my: 1, fontSize: '1.4rem', whiteSpace: 'nowrap' }} key={response.id}>
              <MapsLink
                lat={response.lat || ''}
                lng={response.lng || ''}
                label={response.type === 'check_in' ? 'Check In Location' : 'Check Out Location'}
              />
            </Box>
          ))}
    </>
  );
}
